import {
  CovestroColors,
  covestroMuiTypography,
  createCovestroTheme,
} from "@covestro/mui-theme";
import { createTheme, ThemeProvider } from "@mui/material";
import type React from "react";
import type { ReactNode } from "react";

interface AppThemeProviderProps {
  children: ReactNode;
  primaryColor: string;
  secondaryColor: string;
}

export const AppThemeProvider: React.FC<AppThemeProviderProps> = ({
  children,
  primaryColor,
  secondaryColor,
}) => {
  const baseTheme = createCovestroTheme(
    CovestroColors[primaryColor as keyof typeof CovestroColors],
    CovestroColors[secondaryColor as keyof typeof CovestroColors],
  );
  const baseButtonRoot = baseTheme.components?.MuiButton?.styleOverrides?.root;

  const covestroTheme = createTheme({
    ...baseTheme,
    typography: {
      ...covestroMuiTypography,
      fontFamily: "var(--body-b2-14-regular-font, 'AGRegular')",
    },
    components: {
      ...baseTheme.components,
      MuiButton: {
        ...baseTheme.components?.MuiButton,
        styleOverrides: {
          ...baseTheme.components?.MuiButton?.styleOverrides,
          root: (props) =>
            Object.assign(
              {},
              typeof baseButtonRoot === "function"
                ? baseButtonRoot(props)
                : baseButtonRoot,
              {
                // Figma shows the button as AktivGrotesk-Medium + weight 400,
                // but the design tokens expose only one family ("AktivGrotesk")
                // with no "-Medium" variant — the Medium glyph is selected via
                // font-weight 500 (mapped to AktivGrotesk-Medium.otf in index.css).
                // So weight 500 here renders the same glyph Figma's weight-400 does.
                fontFamily: "var(--body-b2-14-medium-font, 'AGMedium')",
                fontWeight: 500,
                letterSpacing: "var(--body-b2-14-medium-tracking, 0)",
              },
            ),
        },
      },
    },
  });

  return <ThemeProvider theme={covestroTheme}>{children}</ThemeProvider>;
};
